import {
  useSdkConfig,
  setTempPublicKey,
  setTempPrivateKey,
} from '@bankeiro/bankeiro-sdk-security';
import { onTokenChanged } from 'firebase/app-check';

import {
  isAppIntegrityRequired,
  setAppIntegrityRequired,
} from '@portal/services/appIntegrityRuntime';
import { appCheck } from '@portal/services/firebase';

import storeCreator from '@portal/reducers/createStore';
import * as StorageService from '@portal/services/storage';
import * as MessageService from '@portal/services/message';
import centralNavigationService from '@portal/services/navigation';
import { decreaseLoading, increaseLoading } from '@portal/actions/loading';
import { getMeFirstLoad } from '@portal/actions/user';
import { VALIDATE_TOKEN_SUCESS } from '@portal/actions/actionTypes';
import { AuthActions } from '@portal/actions';
import ErrorsMessages from '@portal/enum/errors';
import {
  API_URL,
  KEYCLOAK_CLIENT_ID,
  LOGIN_API_URL,
  USE_SYMMETRIC_ENCRYPTION,
} from '@portal/config/env';
import ApiErrorHandler from '@portal/services/ApiErrorHandler';
import * as AppIntegrityService from '@portal/services/AppIntegrity';

const {
  sdkRSAEncryptionFunctions: { decrypt, encrypt, signData, generateKeyPair },
  sdkSecurityFunctions: {
    getCleanInstance,
    getInstance,
    removeHeader,
    setHeader,
    setHandleApiError,
  },
  sdkValidateDeviceFunctions: { validateDevice },
} = useSdkConfig({
  useSymmetricEncryption: USE_SYMMETRIC_ENCRYPTION,
  apiUrl: API_URL,
  loginApiUrl: LOGIN_API_URL,
  keycloakClientId: KEYCLOAK_CLIENT_ID,
  storageService: StorageService,
  messageService: MessageService,
  centralNavigationService: centralNavigationService,
  errorMessagesEnum: ErrorsMessages,
  getPathname: () => window.location.pathname,
  increaseLoading: () => {
    storeCreator.dispatch(increaseLoading());
  },
  decreaseLoading: () => {
    storeCreator.dispatch(decreaseLoading());
  },
  onValidateTokenSuccess: (payload: boolean) => {
    storeCreator.dispatch({ payload, type: VALIDATE_TOKEN_SUCESS });
  },
  getMeFirstLoad: () => {
    storeCreator.dispatch(getMeFirstLoad());
  },
  logout: () => {
    storeCreator.dispatch(AuthActions.logout());
  },
  useAppIntegrity: true,
  getIntegrityToken: async () => {
    if (!isAppIntegrityRequired()) return null;
    const p = await AppIntegrityService.getIntegrityTokenOrAssertionForLogin();
    return p?.firebaseAppCheckToken ?? null;
  },
});

setHandleApiError(ApiErrorHandler.handle);

const INTEGRITY_FORBIDDEN_CODES: string[] = [
  ErrorsMessages.MISSING_INTEGRITY_TOKEN,
  ErrorsMessages.INVALID_INTEGRITY_TOKEN,
  ErrorsMessages.INTEGRITY_TOKEN_EXPIRED,
  ErrorsMessages.INTEGRITY_VALIDATION_FAILED,
];

const integrityForbiddenCode = (error: unknown): string | null => {
  const err = error as
    | { status?: number; response?: { status?: number; data?: unknown } }
    | null
    | undefined;
  const status = err?.status ?? err?.response?.status;
  if (status !== 403) return null;
  const body = (err?.response?.data ?? err?.response) as
    | { error?: unknown }
    | null
    | undefined;
  const code = body && typeof body === 'object' ? body.error : undefined;
  if (typeof code !== 'string' || !INTEGRITY_FORBIDDEN_CODES.includes(code)) {
    return null;
  }
  return code;
};

const isUserLoggedIn = (): boolean =>
  !!(storeCreator.getState() as { auth?: { accessToken?: string | null } })
    ?.auth?.accessToken;

let integrityLoggingOut = false;

type RetriableRequestConfig = NonNullable<
  Parameters<ReturnType<typeof getInstance>['request']>[0]
> & {
  integrityRetried?: boolean;
};

getInstance().interceptors.response.use(undefined, async (error) => {
  const code = integrityForbiddenCode(error);
  if (!isAppIntegrityRequired() || !code) {
    return Promise.reject(error);
  }
  removeHeader(StorageService.StorageItems.FIREBASE_APP_CHECK, 'local');
  let freshToken: string | null = null;
  try {
    const payload =
      await AppIntegrityService.getIntegrityTokenOrAssertionForLogin(true);
    freshToken = payload?.firebaseAppCheckToken ?? null;
  } catch {
    freshToken = null;
  }
  if (!freshToken) {
    if (
      code === ErrorsMessages.INVALID_INTEGRITY_TOKEN &&
      isUserLoggedIn() &&
      !integrityLoggingOut
    ) {
      integrityLoggingOut = true;
      storeCreator.dispatch(AuthActions.logout());
    }
    return Promise.reject(error);
  }
  integrityLoggingOut = false;
  setHeader(
    StorageService.StorageItems.FIREBASE_APP_CHECK,
    freshToken,
    'local'
  );
  const config = (error as { config?: RetriableRequestConfig } | null)?.config;
  if (!config || config.integrityRetried) {
    return Promise.reject(error);
  }
  const method = config.method ?? 'get';
  if (method.toLowerCase() !== 'get') {
    return Promise.reject(error);
  }
  config.integrityRetried = true;
  config.headers = {
    ...config.headers,
    [StorageService.StorageItems.FIREBASE_APP_CHECK]: freshToken,
  };
  return getInstance().request(config);
});

const syncIntegrityHeader = (token: string | null | undefined): void => {
  if (!token) return;
  setHeader(StorageService.StorageItems.FIREBASE_APP_CHECK, token, 'local');
};

if (appCheck) {
  onTokenChanged(appCheck, ({ token }) => {
    if (!isAppIntegrityRequired()) return;
    syncIntegrityHeader(token);
  });
}

const warmIntegrityToken = async (): Promise<void> => {
  const payload =
    await AppIntegrityService.getIntegrityTokenOrAssertionForLogin();
  syncIntegrityHeader(payload?.firebaseAppCheckToken);
};

export const integrityReady: Promise<void> = getInstance()
  .get<{ required: boolean }>('/app-integrity/config')
  .then(async ({ data }) => {
    const required = data?.required === true;
    setAppIntegrityRequired(required);
    if (!required) {
      removeHeader(StorageService.StorageItems.FIREBASE_APP_CHECK, 'local');
      return;
    }
    await warmIntegrityToken();
  })
  .catch(async () => {
    setAppIntegrityRequired(true);
    await warmIntegrityToken();
  });

const integrityReadyTimeoutMs = 3000;

export const waitIntegrityReady = (): Promise<void> => {
  const timeout = new Promise<void>((resolve) => {
    setTimeout(resolve, integrityReadyTimeoutMs);
  });
  return Promise.race([integrityReady, timeout]);
};

export {
  validateDevice,
  decrypt,
  encrypt,
  signData,
  generateKeyPair,
  getCleanInstance,
  getInstance,
  removeHeader,
  setHeader,
  setTempPublicKey,
  setTempPrivateKey,
};
